import { useEffect, useState } from "react";
import { Download, BookOpen, ChevronRight, FileText, Printer } from "lucide-react";
import adminApi, { getToken } from "@/lib/admin-api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminManual() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    adminApi
      .get("/admin/manual")
      .then((res) => {
        setSections(res.data.sections || []);
        if (res.data.sections?.[0]) setActiveId(res.data.sections[0].id);
      })
      .catch(() => toast.error("Error cargando manual"))
      .finally(() => setLoading(false));
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/admin/manual/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Manual_FlexShade_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Manual descargado");
    } catch {
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const scrollToSection = (id) => {
    setActiveId(id);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) return <div className="p-8 text-neutral-500">Cargando manual...</div>;

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-red-600" /> Manual de uso
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Guía completa del sistema FlexShade para el equipo Eclipse
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
            data-testid="print-manual"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </button>
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-60"
            data-testid="download-pdf"
          >
            <Download className="w-4 h-4" /> {downloading ? "Generando..." : "Descargar PDF"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* TOC sidebar */}
        <aside className="lg:col-span-1 no-print">
          <div className="bg-white border border-neutral-200 rounded-2xl p-4 sticky top-4">
            <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Contenido
            </h3>
            <nav className="space-y-1">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                    activeId === s.id
                      ? "bg-red-600 text-white font-semibold"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-3 manual-content">
          {/* Cover header (visible) */}
          <div className="bg-gradient-to-br from-[#0a0a0a] via-[#1a0505] to-black text-white rounded-2xl p-8 md:p-10 mb-8 text-center">
            <div className="text-red-500 font-black text-3xl md:text-4xl tracking-tight">ECLIPSE</div>
            <div className="text-orange-500 font-bold text-sm md:text-base tracking-[0.3em] mt-1">FLEXSHADE</div>
            <h2 className="mt-6 text-2xl md:text-3xl font-black">Manual de uso de la plataforma</h2>
            <p className="text-white/70 mt-2 text-sm">flexshade.mx · Admin Panel · Eclipse Cubiertas SA de CV</p>
            <p className="text-white/50 mt-4 text-xs">Actualizado: {new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>

          {sections.map((s) => (
            <section
              key={s.id}
              id={`section-${s.id}`}
              className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 mb-4 manual-section"
            >
              <h2 className="section-title text-2xl md:text-3xl font-black text-neutral-900 border-l-4 border-red-600 pl-4 mb-4">
                {s.title}
              </h2>
              <div
                className="manual-html"
                dangerouslySetInnerHTML={{ __html: s.content }}
              />
              {s.screenshots && s.screenshots.length > 0 && (
                <div className="mt-6 space-y-4">
                  {s.screenshots.map((sc, i) => (
                    <figure key={i} className="bg-neutral-50 rounded-xl p-3 border border-neutral-200">
                      <img
                        src={`/manual/${sc.file}`}
                        alt={sc.caption}
                        className="w-full rounded-lg border border-neutral-200"
                        loading="lazy"
                      />
                      <figcaption className="text-center text-xs text-neutral-500 italic mt-2">
                        {sc.caption}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </section>
          ))}

          <div className="bg-neutral-50 rounded-2xl p-6 text-center text-sm text-neutral-600">
            <p className="font-semibold text-neutral-800">Manual FlexShade · Eclipse Cubiertas SA de CV</p>
            <p className="mt-1">¿Dudas técnicas? Contacta al equipo de desarrollo.</p>
          </div>
        </article>
      </div>

      <style>{`
        .manual-html h3 {
          font-size: 1.05rem;
          font-weight: 700;
          color: #DC2626;
          margin: 1.2rem 0 0.4rem;
        }
        .manual-html p {
          font-size: 0.95rem;
          color: #374151;
          line-height: 1.65;
          margin: 0.5rem 0;
        }
        .manual-html ul, .manual-html ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .manual-html li {
          font-size: 0.93rem;
          color: #374151;
          line-height: 1.6;
          margin: 0.25rem 0;
        }
        .manual-html strong { color: #0a0a0a; }
        .manual-html code {
          background: #f3f4f6;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 0.85rem;
          color: #DC2626;
          font-family: "SF Mono", Consolas, monospace;
        }
        .manual-html .callout {
          background: #fff7ed;
          border-left: 4px solid #F97316;
          padding: 12px 16px;
          margin: 16px 0;
          border-radius: 8px;
          font-size: 0.9rem;
          color: #7c2d12;
        }
        .manual-html .callout strong { color: #7c2d12; }
        .manual-html .table {
          width: 100%;
          border-collapse: collapse;
          margin: 12px 0;
          font-size: 0.85rem;
        }
        .manual-html .table th {
          background: #0a0a0a;
          color: white;
          padding: 8px 10px;
          text-align: left;
        }
        .manual-html .table td {
          border: 1px solid #e5e7eb;
          padding: 8px 10px;
          vertical-align: top;
          color: #374151;
        }
        .manual-html .small-text {
          font-size: 0.8rem;
          color: #9ca3af;
          margin-top: 20px;
        }
        @media print {
          .no-print { display: none !important; }
          .manual-content { width: 100% !important; }
          .manual-section { page-break-inside: avoid; box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </div>
  );
}
